import { z } from '../zod.js';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';
import { PaginatedListSchema } from '../shared/paginatedList';
import {
  HistoricalTaxRateSnapshotValidationSchema,
  TaxSourceValidationSchema,
} from '../tax-rate/create';
import { InvoiceTaxBreakdownValidationSchema } from '../invoice/response';
import {
  OrderTypeValidationEnum,
  OrderLifecycleStatusValidationEnum,
  OrderLineItemTypeValidationEnum,
} from './enum';

/**
 * Response shape for the dashboard order endpoints. Validates the order AFTER
 * `serializeObjectIdsAndDates`, so ids and dates are plain strings here.
 *
 * Reference fields are named for the WIRE, not for Mongoose: a scalar ref is
 * `<thing>Id` and an array of refs is `<thing>Ids`, matching every other
 * response schema in this package (invoice `chargeId`, product `fileIds`,
 * coupon `ticketTypeIds`). `OrderModel.ts` names the same fields `product` /
 * `charges` because a Mongoose ref is a populate target — `.populate('product')`
 * replaces the id with the document — but nothing is populated by the time a
 * response is serialized, so a bare name here would promise an object and
 * deliver a string. The names deliberately do NOT line up field-for-field with
 * the model; the snapshots already use this convention internally
 * (`ticketTypeSnapshot.ticketTypeId`, `couponSnapshot.couponId`).
 *
 * Snapshot sub-objects are deliberately loose (`z.record`, and `z.looseObject`
 * on the two schemas themselves): they are frozen copies of
 * price/product/coupon/fee state whose shape is owned by the writer, and
 * re-declaring them here would create a second source of truth that drifts from
 * `apps/api/src/orders/OrderModel.ts`. The exceptions are the tax fields, whose
 * shapes are already shared package-wide (invoice/tax-rate).
 */

const MoneySchema = z.number().optional();

export const OrderLineItemValidationSchema = z
  .looseObject({
    _id: ObjectIdStringValidationSchema.optional(),
    type: OrderLineItemTypeValidationEnum.optional(),
    productId: ObjectIdStringValidationSchema.optional(),
    productPriceId: ObjectIdStringValidationSchema.optional(),
    ticketTypeId: ObjectIdStringValidationSchema.optional(),
    priceSnapshot: z.record(z.string(), z.any()).optional(),
    productSnapshot: z.record(z.string(), z.any()).optional(),
    ticketTypeSnapshot: z.record(z.string(), z.any()).optional(),
    bulkDiscountsSnapshot: z.array(z.record(z.string(), z.any())).optional(),
    couponSnapshot: z.record(z.string(), z.any()).optional(),
    name: z.string().optional(),
    sku: z.string().optional(),
    billingType: z.enum(['one_time', 'recurring']).optional(),
    quantity: MoneySchema,
    unitPrice: MoneySchema,
    payWhatYouWantPrice: MoneySchema,
    discountedFromPrice: MoneySchema,
    subtotal: MoneySchema,
    amount: MoneySchema,
    bulkDiscountAmount: MoneySchema,
    couponDiscountAmount: MoneySchema,
    complimentaryDiscountAmount: MoneySchema,
    discountTotal: MoneySchema,
    taxAmount: MoneySchema,
    taxBehavior: z.enum(['inclusive', 'exclusive']).nullish(),
    taxSource: TaxSourceValidationSchema.nullish(),
    taxBreakdown: z.array(InvoiceTaxBreakdownValidationSchema).nullish(),
    taxRates: z.array(HistoricalTaxRateSnapshotValidationSchema).nullish(),
    amountRefunded: MoneySchema,
    refundedAt: z.iso.datetime().optional(),
    variants: z.array(z.record(z.string(), z.any())).optional(),
    licenseKeyIds: z.array(ObjectIdStringValidationSchema).optional(),
    fileIds: z.array(ObjectIdStringValidationSchema).optional(),
    chargeId: ObjectIdStringValidationSchema.optional(),
    subscriptionId: ObjectIdStringValidationSchema.optional(),
  });

export const OrderValidationSchema = z
  .looseObject({
    _id: ObjectIdStringValidationSchema,
    orderId: z.string().optional(),
    type: OrderTypeValidationEnum.optional(),
    status: OrderLifecycleStatusValidationEnum.optional(),
    merchantStatus: z.string().optional(),
    reason: z.string().optional(),
    canceledAt: z.iso.datetime().optional(),
    canceledById: ObjectIdStringValidationSchema.optional(),
    pageType: z.string().optional(),

    sellerId: ObjectIdStringValidationSchema.optional(),
    pageId: ObjectIdStringValidationSchema.optional(),
    customerId: ObjectIdStringValidationSchema.optional(),
    customerEmail: z.string().optional(),
    customerName: z.string().optional(),

    lineItems: z.array(OrderLineItemValidationSchema).optional(),

    currency: z.string().optional(),
    subtotal: MoneySchema,
    discountTotal: MoneySchema,
    isComplimentary: z.boolean().optional(),
    complimentaryDiscountAmount: MoneySchema,
    couponSnapshot: z.record(z.string(), z.any()).optional(),
    bulkDiscountsSnapshot: z.array(z.record(z.string(), z.any())).optional(),
    feesSnapshot: z.array(z.record(z.string(), z.any())).optional(),
    fees: z.array(z.record(z.string(), z.any())).optional(),
    taxAmount: MoneySchema,
    total: MoneySchema,
    amountExcludingTax: MoneySchema,
    amountUsd: MoneySchema,
    amountPaid: MoneySchema,
    amountDue: MoneySchema,
    amountRefunded: MoneySchema,
    refundedAt: z.iso.datetime().optional(),
    paidAt: z.iso.datetime().optional(),
    backfilledAt: z.iso.datetime().optional(),

    chargeIds: z.array(ObjectIdStringValidationSchema).optional(),
    subscriptionIds: z.array(ObjectIdStringValidationSchema).optional(),
    invoiceIds: z.array(ObjectIdStringValidationSchema).optional(),
    submissionIds: z.array(ObjectIdStringValidationSchema).optional(),

    billing: z.record(z.string(), z.any()).optional(),
    shipping: z.record(z.string(), z.any()).optional(),
    fields: z.array(z.record(z.string(), z.any())).optional(),

    // Checkout attribution, written by all three order builders.
    sessionId: ObjectIdStringValidationSchema.optional(),
    visitId: ObjectIdStringValidationSchema.optional(),

    livemode: z.boolean().optional(),
    locale: z.string().optional(),

    createdAt: z.iso.datetime().optional(),
    updatedAt: z.iso.datetime().optional(),
  });

export const ListOrdersResponseValidationSchema = PaginatedListSchema(OrderValidationSchema);

export type OrderResponse = z.infer<typeof OrderValidationSchema>;
export type ListOrdersResponse = z.infer<typeof ListOrdersResponseValidationSchema>;
